import axios from "axios";
import { BASE_URL } from "../Global";
import useAuthStore from "../Store/AuthStore";

// ─── helpers ─────────────────────────────────────────────────────────────────

const authHeaders = () => {
    const { token } = useAuthStore.getState();
    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
};

/** Map CartStore items → backend { giftId, quantity } format */
const toBackendItems = (cartItems) =>
    cartItems.map((item) => ({ giftId: item.id, quantity: item.quantity }));

const handleError = (error) => ({
    success: false,
    message:
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "Network error – failed to connect to server",
});

// ─── 1. Preview cart totals (no charge, no PaymentIntent) ────────────────────
// Call whenever the cart changes to show server-verified subtotal/shipping/tax.

export const previewCartAPI = async (cartItems, country = "DE", currency = "eur") => {
    try {
        const response = await axios.post(
            `${BASE_URL}/api/checkout/preview`,
            { items: toBackendItems(cartItems), country, currency },
            { headers: { "Content-Type": "application/json" } }
        );
        return { success: true, summary: response.data.summary };
    } catch (error) {
        return handleError(error);
    }
};

// ─── 2. Create PaymentIntent + pending Order ──────────────────────────────────
// Call when the user clicks "Continue to Payment" (after form validation).
// Returns clientSecret that Stripe.js uses to render the PaymentElement.

export const createPaymentIntentAPI = async (
    cartItems,
    customerDetails,
    currency = "eur",
    idempotencyKey
) => {
    try {
        const response = await axios.post(
            `${BASE_URL}/api/checkout/payment-intent`,
            {
                items: toBackendItems(cartItems),
                customerDetails,
                currency,
                idempotencyKey,
            },
            { headers: authHeaders() }
        );
        return {
            success: true,
            clientSecret: response.data.clientSecret,
            orderId: response.data.orderId,
            paymentIntentId: response.data.paymentIntentId,
            summary: response.data.summary,
        };
    } catch (error) {
        return handleError(error);
    }
};

// ─── 3. Update PaymentIntent amount if cart changes after PI was created ──────

export const updatePaymentIntentAPI = async (
    orderId,
    cartItems,
    country = "DE",
    currency = "eur"
) => {
    try {
        const response = await axios.put(
            `${BASE_URL}/api/checkout/payment-intent/${orderId}`,
            { items: toBackendItems(cartItems), country, currency },
            { headers: { "Content-Type": "application/json" } }
        );
        return { success: true, updated: response.data.updated, summary: response.data.summary };
    } catch (error) {
        return handleError(error);
    }
};

// ─── 4. Retrieve a single order (requires auth) ───────────────────────────────

export const getOrderAPI = async (orderId) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/api/checkout/order/${orderId}`,
            { headers: authHeaders() }
        );
        return { success: true, order: response.data.order };
    } catch (error) {
        return handleError(error);
    }
};

// ─── 5. Cancel a pending order (requires auth) ────────────────────────────────

export const cancelOrderAPI = async (orderId) => {
    try {
        const response = await axios.put(
            `${BASE_URL}/api/checkout/cancel/${orderId}`,
            {},
            { headers: authHeaders() }
        );
        return { success: true, message: response.data.message };
    } catch (error) {
        return handleError(error);
    }
};

// ─── 6. Get all orders for a user (requires auth) ────────────────────────────

export const getUserOrdersAPI = async (userId) => {
    try {
        const response = await axios.get(
            `${BASE_URL}/api/checkout/orders/user/${userId}`,
            { headers: authHeaders() }
        );
        return { success: true, orders: response.data.orders };
    } catch (error) {
        return handleError(error);
    }
};
